import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
// import firstCharToUpper from "../helpers/stringFormat";
import CostValueDriverLaborForm from "./CostValueDriverLaborForm";
import { getIconFromDriverName } from "./iconFunctions";
import ModalElement from "../Modal";
import AddEmployeeData from "./AddEmployeeData";
import { toolTipUserIcon } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const CostValueDriverLabor = ({ id, valueModel, costs, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();
    
    const [open, setOpen] = React.useState(false);
    const [selectedCost, setSelectedCost] = useState(Object.keys(costs)[0])
    const [deleteLaborDriverPopupOpen, setDeleteLaborDriverPopupOpen] = useState(false);
    const [deleteLaborDriverId, setDeleteLaborDriverId] = useState(null);
    const [editLaborDriverId, setEditLaborDriverId] = useState(null);
    const [addEmployeeModalOpen, setAddEmployeeModalOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCostChange = (event) => {
        setSelectedCost(event.target.value)
        if (editLaborDriverId) {
            setEditLaborDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteLaborDriver = (id) => {
        setDeleteLaborDriverId(id)
        setDeleteLaborDriverPopupOpen(true)
    }

    const editLaborDriver = (id) => {
        setEditLaborDriverId(id)
        let laborDriver = valueModel.cost_value_driver.labor_savings.find(laborDriver => laborDriver.id === id)
        setSelectedCost(laborDriver.sub_cost)
    }

    const toggleAddEmployeeModalOpen = () => {
        setAddEmployeeModalOpen(!addEmployeeModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddEmployeeModal() {
        return (
            <ModalElement
                modalOpen={addEmployeeModalOpen}
                modalWidth={1000}
                handleClose={toggleAddEmployeeModalOpen}
                header="Add Employee Costs"
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddEmployeeData
                        id={id}
                        industry={valueModel?.industry}
                        type='labor'
                        cost_category={driver}
                        employeeCategories={costs}
                        handleClose={toggleAddEmployeeModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitLabor = async (values, driver, cost, { setSubmitting, setErrors }) => {
        values.id = editLaborDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.cost = driver
        values.sub_cost = cost
        let success = false

        if (editLaborDriverId) {
            const result = await dispatch(editDriver('labor', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditLaborDriverId(null)
            }
        }
        else {
            const result = await dispatch(addDriver('labor', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const handleResetLabor = () => {
        setEditLaborDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const laborDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        // { id: 'sub_cost', label: 'Employee', format: (value) => formatString(value), minWidth: 100, action: false },
        // { id: 'multiple_type', label: 'Type', format: (value) => firstCharToUpper(value), minWidth: 100, action: false },
        { id: 'hours_saved', label: 'Hours per employee', minWidth: 100, action: false },
        { id: 'occurrence', label: 'Occurrence', format: (value) => value + ' %', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createLaborDriverData = (id, outcome, driver_name, sub_cost, multiple_type, cost_per_multiple, hours_saved, occurrence, multiple_value, positive_driver, actions) => {
        hours_saved = `${hours_saved} hours ${positive_driver ? 'saved' : 'extra'}`
        return { id, outcome, driver_name, sub_cost, multiple_type, cost_per_multiple, hours_saved, occurrence, multiple_value, positive_driver, actions };
    }

    const laborDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editLaborDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteLaborDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const laborDriverRows = valueModel.cost_value_driver.labor_savings
        .filter(driver => !outcome || driver.outcome === outcome)
        .map(driver =>
            createLaborDriverData(
                driver.id,
                driver.outcome,
                driver.driver_name,
                driver.sub_cost,
                driver.multiple_type,
                driver.cost_per_multiple,
                driver.hours_saved,
                driver.occurrence,
                driver.multiple_value,
                driver.positive_driver,
                laborDriverActions(driver.id)
            )
        );

    const laborDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={laborDriverColumns}
                    rows={laborDriverRows}
                    selectedId={editLaborDriverId}
                />
            </React.Fragment>
        )
    }

    const laborDriverFormActions = () => {
        return (
            <React.Fragment>
                {editLaborDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editLaborDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetLabor}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Labor Savings
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddEmployeeModalOpen} sx={{ textTransform: 'none' }}>Add Employees</Button>
            </Box>

            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Employee Category
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedCost}
                        onChange={handleCostChange}
                        size='small'
                    >
                        {Object.keys(costs).map((cost) => (
                            <MenuItem key={cost} value={cost}>
                                {/* {formatString(cost)} */}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(cost)}
                                    {costs[cost]?.user_created && toolTipUserIcon()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <CostValueDriverLaborForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                selectedCost={selectedCost}
                costStructure={costs[selectedCost]}
                editLaborDriverId={editLaborDriverId}
                editLaborDriver={valueModel.cost_value_driver.labor_savings.find(laborDriver => laborDriver.id === editLaborDriverId)}
                formActions={laborDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitLabor(values, driver, selectedCost, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {laborDriverTable()}
            <DeletePopup open={deleteLaborDriverPopupOpen} onClose={() => setDeleteLaborDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('labor', id, deleteLaborDriverId))} />
            {renderAddEmployeeModal(selectedCost)}
        </Box>
    )
}

export default CostValueDriverLabor